import { useCallback, useMemo } from 'react';
import { useFormik } from 'formik';
import { ModalProps } from 'antd';
import { isEmpty } from 'lodash';
import * as Yup from 'yup';
import { FileTextOutlined } from '@ant-design/icons';
import { StyledIconWrapper, StyledModal, StyledModalContent, StyledModalFooter } from './styles';
import Button from 'components/new-ui/base/button/Button';
import DeclarationInput from 'components/new-ui/composed/declarations/formInput/DeclarationInput';
import DeclarationSelect, { SelectOption } from 'components/new-ui/composed/declarations/formSelect/DeclarationSelect';
import FormTextArea from 'components/new-ui/composed/formTextArea/FormTextArea';
import useFormUtils from 'hooks/useFormUtils';
import { DeclarationDocumentResponse } from 'store/declarations/declaration';
import { updateCmsFile } from 'store/documents/client';
import {
    createDocumentAes,
    createDocumentAis,
    createDocumentNcts,
    updateDocumentAes,
    updateDocumentAis,
    updateDocumentNcts,
} from 'store/file-upload/client';
import {
    documentFormValidationSchemaCommon,
    documentFormValidationSchemaAes,
    documentFormValidationSchemaAis,
    documentFormValidationSchemaNcts,
} from './file-validation.schema';
import Notification from 'components/ui/base/notification/Notification';
import { getErrorMessage } from 'utils/getErrorMessage';
import { DeclarationDocument } from 'views/declarations/declaration-tabs/documentation/types';

interface Props extends ModalProps {
    isEditing: boolean;
    file: DeclarationDocumentResponse | undefined;
    declarationDocument: DeclarationDocument | undefined;
    declarationId: string | undefined;
    documentTypeOptions: SelectOption[] | undefined;
    listCmsFiles: () => void | Promise<void>;
}

const DeclarationDocumentFormModal = ({
    isEditing,
    open,
    file,
    declarationId,
    declarationDocument,
    documentTypeOptions,
    onCancel,
    listCmsFiles,
    ...rest
}: Props) => {
    const { isAes, isAis, isNcts } = useFormUtils();

    const formikInitialValues = useMemo(
        () => ({
            // uploaded file / CMS values
            fileSize: file?.fileSize,
            filename: file?.filename,
            documentType: file?.documentType,
            link: file?.link,

            // uploaded document values (AIS / AES / NCTS)
            ...(isAes || isNcts
                ? {
                      description: declarationDocument?.fileInfo?.description,
                      documentComplementaryInformation: declarationDocument?.fileInfo?.documentComplementaryInformation,
                  }
                : {
                      documentReference: declarationDocument?.fileInfo?.documentReference,
                      documentIdentifier: declarationDocument?.fileInfo?.documentIdentifier,
                      documentDescription: declarationDocument?.fileInfo?.documentDescription,
                  }),
        }),
        [
            file?.documentType,
            file?.fileSize,
            file?.filename,
            file?.link,
            declarationDocument?.fileInfo?.description,
            declarationDocument?.fileInfo?.documentComplementaryInformation,
            declarationDocument?.fileInfo?.documentDescription,
            declarationDocument?.fileInfo?.documentIdentifier,
            declarationDocument?.fileInfo?.documentReference,
            isAes,
            isNcts,
        ]
    );

    const formik = useFormik<any>({
        initialValues: formikInitialValues ?? {},
        validateOnMount: true,
        validationSchema: Yup.object().shape({
            ...documentFormValidationSchemaCommon,
            ...(isAes
                ? documentFormValidationSchemaAes
                : isAis
                ? documentFormValidationSchemaAis
                : documentFormValidationSchemaNcts),
        }),
        enableReinitialize: true,
        onSubmit: () => {},
    });

    const handleUploadDocument = useCallback(async () => {
        const errors = await formik.validateForm();
        formik.setErrors(errors as any);

        if (!isEmpty(errors) || !declarationId || !file?.id) return;

        const documentBody = {
            fileId: file.id,
            fileReference: formik.values.link,
            fileName: formik.values.filename,
            fileInfo: {
                documentType: formik.values.documentType,
                ...(isAes || isNcts
                    ? {
                          documentComplementaryInformation: formik.values.documentComplementaryInformation,
                          description: formik.values.description,
                      }
                    : {
                          documentReference: formik.values.documentReference,
                          documentIdentifier: formik.values.documentIdentifier,
                          documentDescription: formik.values.documentDescription,
                      }),
            },
        };

        const cmsFileBody = {
            id: file.id,
            type: formik.values.documentType,
            filename: formik.values.filename,
            description: formik.values.documentDescription || formik.values.description,
        };

        try {
            if (declarationDocument) {
                const updateDocument = isAes ? updateDocumentAes : isAis ? updateDocumentAis : updateDocumentNcts;
                await updateDocument(
                    { ...documentBody, id: declarationDocument.id, status: declarationDocument.status },
                    declarationId
                );

                Notification({
                    type: 'success',
                    messageTitle: 'Operation Successful',
                    description: 'Successfully updated declaration file.',
                    placement: 'top',
                });
            } else {
                const createDocument = isAes ? createDocumentAes : isAis ? createDocumentAis : createDocumentNcts;
                await createDocument(documentBody, declarationId);

                Notification({
                    type: 'success',
                    messageTitle: 'Operation Successful',
                    description: 'Successfully updated declaration file.',
                    placement: 'top',
                });
            }

            // Keep CMS metadata in sync
            await updateCmsFile(cmsFileBody.id, cmsFileBody);
            await Promise.resolve(listCmsFiles?.());
            onCancel?.({} as any);
        } catch (err) {
            const error = getErrorMessage(err);
            Notification({
                type: 'error',
                messageTitle: 'Upload error',
                description: error || 'Could not upload file.',
                placement: 'top',
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik, declarationId, file?.id, isAes, isNcts, onCancel, listCmsFiles, declarationDocument?.id, isAis]);

    const handleCancel = async () => {
        if (!isEditing && file?.id && declarationId) {
            try {
                const documentBody = {
                    fileId: file.id,
                    fileReference: file.link,
                    fileName: file.filename,
                };

                const createDocument = isAes ? createDocumentAes : isAis ? createDocumentAis : createDocumentNcts;
                await createDocument(documentBody, declarationId);
                await Promise.resolve(listCmsFiles?.());
            } catch (err) {
                console.warn('Could not attach document to declaration on cancel:', getErrorMessage(err));
            }
        }

        onCancel?.({} as any);
    };

    const refNumber = '2.3';
    const title = isEditing ? 'Update document' : 'Upload document';

    return (
        <StyledModal
            open={open}
            title={title}
            maskClosable={false}
            footer={
                <StyledModalFooter>
                    <Button onClick={handleCancel}>Cancel</Button>
                    <Button onClick={handleUploadDocument} type="primary" disabled={!(formik.isValid && formik.dirty)}>
                        {title}
                    </Button>
                </StyledModalFooter>
            }
            {...rest}
            onCancel={handleCancel}
        >
            <StyledModalContent>
                <StyledIconWrapper>
                    <FileTextOutlined
                        style={{
                            fontSize: 42,
                        }}
                    />
                </StyledIconWrapper>

                <div
                    style={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 12,
                    }}
                >
                    <DeclarationInput
                        required
                        label="File Name"
                        fieldProps={formik.getFieldProps('filename')}
                        fieldMeta={formik.getFieldMeta('filename')}
                        condensed
                        refNumber={refNumber}
                        tooltip="File Name"
                    />
                    <DeclarationSelect
                        required
                        label="Document Type"
                        fieldProps={formik.getFieldProps('documentType')}
                        fieldMeta={formik.getFieldMeta('documentType')}
                        condensed
                        refNumber={refNumber}
                        tooltip="Document Type"
                        selectOptions={documentTypeOptions}
                        hideCodelistMenu
                    />

                    {isAes || isNcts ? (
                        <>
                            <DeclarationInput
                                required
                                label="Document Complementary Information"
                                fieldProps={formik.getFieldProps('documentComplementaryInformation')}
                                fieldMeta={formik.getFieldMeta('documentComplementaryInformation')}
                                condensed
                                refNumber={refNumber}
                                tooltip="Document Complementary Information"
                            />
                            <FormTextArea
                                required
                                label="Description"
                                fieldProps={formik.getFieldProps('description')}
                                fieldMeta={formik.getFieldMeta('description')}
                                refNumber={refNumber}
                                tooltip="Description"
                                condensed
                                rows={2}
                            />
                        </>
                    ) : (
                        <>
                            <DeclarationInput
                                label="Document Identifier"
                                fieldProps={formik.getFieldProps('documentIdentifier')}
                                fieldMeta={formik.getFieldMeta('documentIdentifier')}
                                condensed
                                required
                                refNumber={refNumber}
                                tooltip="Document Identifier"
                            />
                            <FormTextArea
                                label="Document Reference"
                                fieldProps={formik.getFieldProps('documentReference')}
                                fieldMeta={formik.getFieldMeta('documentReference')}
                                placeholder="Enter Document reference"
                                required
                                refNumber={refNumber}
                                tooltip="Document Reference"
                                condensed
                                rows={2}
                            />
                            <FormTextArea
                                required
                                label="Document Description"
                                fieldProps={formik.getFieldProps('documentDescription')}
                                fieldMeta={formik.getFieldMeta('documentDescription')}
                                refNumber={refNumber}
                                tooltip="Document Description"
                                placeholder="Enter Document Description"
                                condensed
                                rows={2}
                            />
                        </>
                    )}
                </div>
            </StyledModalContent>
        </StyledModal>
    );
};

export default DeclarationDocumentFormModal;
